import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import styles from './field.module.scss'
import { formatDate } from '../../../../shared/datetime'

const PUNKTE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

/**
 * Ein Feld des Abgaberasters mit seiner Bewertung.
 *
 * Der Text der Schüler:innen steht vollständig da — eine Bewertung, für die
 * man erst aufklappen muss, wird nicht gelesen. Darunter elf Knöpfe von 0 bis
 * 10 statt eines Schiebers: Ein Schieber trifft am Touchpad die 7 nicht
 * zuverlässig, und die Punktzahl ist hier eine Note, keine Menge.
 *
 * Gespeichert wird sofort beim Klick. Ein Speichern-Knopf über fünfzehn
 * Feldern hieße, dass eine unterbrochene Bewertung nichts hinterlässt.
 *
 * Steht unter `common`, weil zwei Rollen dasselbe Feld sehen: die Lehrkraft
 * zum Bewerten, die Administration zum Nachlesen (`readOnly`). Zwei Bausteine
 * nebeneinander hätten dieselbe Bewertung unterschiedlich dargestellt.
 *
 * **Beim Nachlesen bleibt die Skala stehen**, nur eben ohne Knöpfe. Eine
 * Punktzahl als bloße Zahl sagt wenig; erst neben den zehn übrigen ist zu
 * sehen, wie streng die Lehrkraft war. Darunter steht, wer wann bewertet hat.
 */

const datum = wert =>
	formatDate(wert, { day: '2-digit', month: '2-digit', year: 'numeric' }) || null
export default function ScoreField({ field, students, readOnly, onScore }) {
	const [wert, setWert] = useState(field.score)
	const [stand, setStand] = useState(null)

	useEffect(() => setWert(field.score), [field.score])

	const waehle = punkte => {
		setWert(punkte)
		setStand('speichert')
		Promise.resolve(onScore(field.no, punkte))
			.then(() => setStand('gespeichert'))
			.catch(() => {
				setWert(field.score)
				setStand('fehler')
			})
	}

	const namen = students.filter(student => (field.assigned || []).includes(student.id)).map(student => student.name)

	return (
		<article className={styles.root}>
			<header className={styles.head}>
				<span className={styles.no}>{field.no}</span>
				<div>
					<h4 className={styles.title}>{field.title}</h4>
					<p className={styles.question}>{field.question}</p>
				</div>
				{wert !== null && wert !== undefined && <span className={styles.badge}>{wert} / 10</span>}
			</header>

			<div className={clsx(styles.text, !field.text && styles.textEmpty)}>
				{field.text || 'Dieses Feld ist leer geblieben.'}
			</div>

			<p className={styles.by}>
				{namen.length ? `Daran gearbeitet: ${namen.join(', ')}` : 'Niemandem zugeordnet.'}
			</p>

			{readOnly ? (
				<div className={styles.scale}>
					<span className={styles.scaleLabel}>Punkte</span>
					{PUNKTE.map(punkte => (
						<span
							key={punkte}
							className={clsx(
								styles.point,
								styles.pointStatic,
								wert === punkte && styles.pointOn,
								wert === null && styles.pointOff
							)}
						>
							{punkte}
						</span>
					))}
					{(wert === null || wert === undefined) && <span className={styles.state}>noch nicht bewertet</span>}
				</div>
			) : (
				<div className={styles.scale}>
					<span className={styles.scaleLabel}>Punkte</span>
					{PUNKTE.map(punkte => (
						<button
							key={punkte}
							type="button"
							aria-pressed={wert === punkte}
							className={clsx(styles.point, wert === punkte && styles.pointOn)}
							onClick={() => waehle(punkte)}
						>
							{punkte}
						</button>
					))}
					<span className={clsx(styles.state, stand === 'fehler' && styles.stateError)}>
						{stand === 'speichert' && 'wird gespeichert …'}
						{stand === 'gespeichert' && 'gespeichert'}
						{stand === 'fehler' && 'konnte nicht gespeichert werden'}
					</span>
				</div>
			)}

			{field.scoredByName && (
				<p className={styles.by}>
					Bewertet von {field.scoredByName}
					{field.scoredAt ? ` am ${datum(field.scoredAt)}` : ''}.
				</p>
			)}
		</article>
	)
}

ScoreField.propTypes = {
	field: PropTypes.object.isRequired,
	students: PropTypes.array.isRequired,
	readOnly: PropTypes.bool,
	// Nur zum Bewerten noetig — beim Nachlesen (`readOnly`) gibt es nichts zu
	// speichern, und ein Pflichtfeld dafuer waere eine leere Zusage.
	onScore: PropTypes.func
}

ScoreField.defaultProps = {
	readOnly: false,
	onScore: () => {}
}
